// ===== Main App =====
import React from 'react';
import { HashRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Icon } from './icons.jsx';
import { MONTHS, STAGE_NAMES, TIPO_CLIENTE, TIPO_CLIENTE_BY_ID, TODAY, brl, brlCompact } from './data.jsx';
import { ToastLayer } from './primitives.jsx';
import { StoreProvider, useStore } from './store.jsx';
import { LoginScreen, ConfirmarAcesso, lerSessao, encerrarSessao } from './login.jsx';
import { supabase, perfilAtual, sairLocal, linkDeAcesso } from './data/supabase.js';
import { enderecoSemOLink } from './link-de-acesso.js';
import { useMonitorInatividade, marcarLogoutPorInatividade, limparAtividade } from './inatividade.js';
import { criarApi } from './data/api.js';
import { ROLE_LABELS, ROLE_DESC, ROLE_HOME, usePermission } from './auth.jsx';
import { ClientesPage, ClienteDrawer, TipoChip } from './clientes.jsx';
import { ClientePage } from './cliente-page.jsx';
import { ProdutosPage, ProdutoDrawer } from './produtos.jsx';
import {
  NewLeadModal, NewAlertModal, AddNoteModal, EditLeadModal, NewWebhookModal,
  ApiLogDetailModal, CohortCellModal, NewClienteModal, EditTaskModal, PdfPreviewModal, Select, MetaModal,
} from './modals.jsx';
import { PropostaBuilderModal } from './proposta.jsx';
import { EditClienteModal } from './cliente-edit.jsx';
import { Sidebar, Header, FAB, CommandPalette } from './shell.jsx';
import { KanbanBoard, LostReasonModal, FunnelSwitcher } from './kanban.jsx';
import { LeadDrawer, NegocioPage } from './drawer.jsx';
import { ResumoPage } from './dashboard.jsx';
import { SacPage, NewChamadoModal } from './sac.jsx';
import { telaOuEmDesenvolvimento } from './tela-em-desenvolvimento.jsx';
import { ErroNaTela } from './erro-na-tela.jsx';

// O acesso a dados é criado UMA vez, aqui, e desce como prop. store.jsx não
// pode importá-lo: instanciar o cliente no escopo daquele módulo quebra o
// teste de contrato que importa os 44 arquivos.
const api = criarApi(supabase);

// ===== Telas carregadas sob demanda (Tarefa 14) =====
// O pacote inicial carregava as 15 telas mesmo para quem só abre o Resumo.
// Estas 9 são exclusivas de rota — `node tests/unit/classificar-imports-rota.mjs`
// classifica cada símbolo — então saem do pacote inicial sem efeito colateral.
//
// Ficaram DE FORA de propósito, e não por esquecimento:
//   clientes.jsx, produtos.jsx, sac.jsx  -> exportam também ClienteDrawer,
//     ProdutoDrawer e NewChamadoModal, usados fora das rotas. O arquivo entraria
//     no pacote inicial de qualquer forma, e separar só a página não renderia
//     nada — exigiria partir arquivos que hoje funcionam.
//   dashboard.jsx (Resumo) -> é a tela inicial; adiar é trocar peso por espera.
//
// Ao adicionar rota nova: se a tela for só de rota, use lazy() aqui.
const AlertasCalendarioPage = React.lazy(() => import('./calendar.jsx').then((m) => ({ default: m.AlertasCalendarioPage })));
const ReportsPage    = React.lazy(() => import('./reports.jsx').then((m) => ({ default: m.ReportsPage })));
const GestaoPage     = React.lazy(() => import('./gestao.jsx').then((m) => ({ default: m.GestaoPage })));
const FinanceiroPage = React.lazy(() => import('./financeiro-relatorios.jsx').then((m) => ({ default: m.FinanceiroPage })));
const PosVendaPage   = React.lazy(() => import('./posvenda.jsx').then((m) => ({ default: m.PosVendaPage })));
const ChamadoPage    = React.lazy(() => import('./chamado-page.jsx').then((m) => ({ default: m.ChamadoPage })));
const AdminPage      = React.lazy(() => import('./admin.jsx').then((m) => ({ default: m.AdminPage })));
const ApiLogsPage    = React.lazy(() => import('./admin.jsx').then((m) => ({ default: m.ApiLogsPage })));
const WebhooksPage   = React.lazy(() => import('./admin.jsx').then((m) => ({ default: m.WebhooksPage })));
const PerfilPage     = React.lazy(() => import('./perfil.jsx').then((m) => ({ default: m.PerfilPage })));
const AjustesPage    = React.lazy(() => import('./ajustes.jsx').then((m) => ({ default: m.AjustesPage })));

const { useState: useStateApp, useEffect: useEffectApp, useMemo: useMemoApp } = React;

// ===== Usuário autenticado (Tarefa 12.5) =====
// Fonte única = sessao.user (Supabase Auth) — o CRM não tem mais um usuário fixo
// de protótipo. Nome/iniciais são inferidos do e-mail corporativo; papel começa
// como "closer" (menor privilégio) até existir sincronização com a tabela
// `profiles` — mesmo default seguro que usePermission já aplica quando o papel
// vem vazio, nunca abre acesso administrativo por engano.
function usuarioDaSessao(sessao) {
  const email = sessao?.user?.email || "";
  const local = email.split("@")[0] || "";
  const nome = local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ") || email || "Usuário";
  const initials = nome
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("") || "?";
  return {
    id: sessao?.user?.id || null,
    nome,
    initials,
    email,
    papel: "closer", // menor privilégio até a Admin promover pelo cadastro de usuários
    idPessoaVendedor: null,
  };
}

// ===== Usuário a partir do PERFIL (banco) =====
// Substitui o papel fixo acima. `usuarioDaSessao` continua servindo só para
// exibir nome/iniciais na tela de espera, onde ainda não há perfil liberado.
// Traduz as colunas de `profiles` para o formato que o store já consome —
// o store não conhece o banco, e essa fronteira é de propósito.
export function usuarioDoPerfil(perfil, sessao) {
  const base = usuarioDaSessao(sessao);
  return {
    id: perfil.id,
    nome: perfil.nome || base.nome,
    initials: perfil.iniciais || base.initials,
    email: perfil.email || base.email,
    papel: String(perfil.papel || "closer").toLowerCase(),
    idPessoaVendedor: perfil.id_pessoa_vendedor ?? null,
    // O telefone gravado em Meu perfil tem de voltar depois do F5 (achado no aceite no navegador, 02/10).
    telefone: perfil.telefone || "",
  };
}

// ===== Guarda de rota =====
// O menu esconde o que o papel não pode abrir; a guarda impede o acesso direto
// pela URL (#/gestao, #/admin…). Sem ela, esconder o item não é controle.
function SemAcesso({ perm }) {
  const store = useStore();
  const papel = (store.me?.papel || '').toLowerCase();
  return (
    <div className="main">
      <div className="acesso-negado">
        <div className="an-ic"><Icon name="lock" size={22} /></div>
        <div className="an-tt">Área restrita</div>
        <div className="an-sub">
          Seu login — <strong>{(ROLE_LABELS && ROLE_LABELS[papel]) || papel}</strong> — não tem acesso a esta área.
          {ROLE_DESC && ROLE_DESC[papel] ? <><br />{ROLE_DESC[papel]}.</> : null}
        </div>
        <div className="an-perm mono">{perm}</div>
        <div className="an-note">Se você precisa desta área para trabalhar, peça a liberação ao Admin.</div>
      </div>
    </div>
  );
}

// Espera entre clicar no menu e a tela sob demanda chegar (Tarefa 14). Fica
// só na área da rota — menu e cabeçalho continuam na tela, então a navegação
// não "pisca" inteira. Discreta de propósito: em rede boa dura milissegundos,
// e um spinner grande faria o app parecer mais lento do que é.
function TelaCarregando() {
  return (
    <div className="main">
      <div style={{ padding: 24, color: "var(--color-ink-faint)", fontSize: 13 }}>Carregando…</div>
    </div>
  );
}

// ===== Barreira de entrada (perfil ausente ou não liberado) =====
// É esta tela que dá sentido ao `ativo = false` com que a migration 0025 cria
// todo perfil novo. Sem ela o campo seria só enfeite na lista do Admin: até
// hoje um perfil inativo entrava no CRM normalmente.
// Vale também para quem entrou ANTES do gatilho existir e por isso não tem
// perfil nenhum — ausência de perfil é tratada como não liberado, nunca como
// papel vazio seguindo adiante.
function AguardandoLiberacao({ usuario, semPerfil, onSair }) {
  return (
    <div className="main">
      <div className="acesso-negado">
        <div className="an-ic"><Icon name="clock" size={22} /></div>
        <div className="an-tt">Acesso aguardando liberação</div>
        <div className="an-sub">
          Seu login <strong>{usuario?.email}</strong> foi reconhecido, mas ainda não foi
          liberado para usar o CRM.
        </div>
        <div className="an-note">
          {semPerfil
            ? "Não há cadastro vinculado a este e-mail. Peça ao administrador para criar o seu acesso."
            : "O administrador precisa ativar o seu usuário na tela de Admin. Assim que ele fizer isso, basta recarregar a página."}
        </div>
        <div style={{ marginTop: 16 }}>
          <button className="btn" onClick={onSair}>Sair</button>
        </div>
      </div>
    </div>
  );
}

// Falha ao consultar o perfil não pode virar "entra como closer": sem saber o
// papel, o certo é parar e deixar tentar de novo. Falha fechada, mas com saída.
function FalhaAoCarregarPerfil({ erro, onTentarDeNovo, onSair }) {
  return (
    <div className="main">
      <div className="acesso-negado">
        <div className="an-ic"><Icon name="alert-triangle" size={22} /></div>
        <div className="an-tt">Não foi possível confirmar o seu acesso</div>
        <div className="an-sub">
          O CRM não conseguiu consultar o seu cadastro agora. Isso costuma ser conexão.
        </div>
        <div className="an-perm mono">{erro}</div>
        <div style={{ marginTop: 16, display: "flex", gap: 8, justifyContent: "center" }}>
          <button className="btn primary" onClick={onTentarDeNovo}>Tentar de novo</button>
          <button className="btn" onClick={onSair}>Sair</button>
        </div>
      </div>
    </div>
  );
}

// Aviso de 1 min antes do logout por inatividade. Aparece sobre a tela; sair
// agora encerra na hora, continuar zera o relogio (marcarAtividade). Ver
// src/inatividade.js.
function AvisoInatividade({ restanteMs, onContinuar, onSair }) {
  const seg = Math.max(0, Math.ceil((restanteMs || 0) / 1000));
  return (
    <div className="inat-overlay" role="alertdialog" aria-modal="true" aria-labelledby="inat-tt">
      <div className="inat-card">
        <div className="an-ic"><Icon name="clock" size={22} /></div>
        <div className="an-tt" id="inat-tt">Você ainda está aí?</div>
        <div className="an-sub">
          Por segurança, sua sessão será encerrada por inatividade em <strong>{seg}s</strong>.
        </div>
        <div style={{ marginTop: 16, display: "flex", gap: 8, justifyContent: "center" }}>
          <button className="btn" onClick={onSair}>Sair agora</button>
          <button className="btn primary" onClick={onContinuar}>Continuar conectado</button>
        </div>
      </div>
    </div>
  );
}

function Guard({ perm, children }) {
  const pode = usePermission(perm);
  return pode ? children : <SemAcesso perm={perm} />;
}

// Rota inicial: cada papel cai no painel que usa.
function HomeRoute(props) {
  const store = useStore();
  const pode = usePermission('dashboard.view');
  const papel = (store.me?.papel || '').toLowerCase();
  if (pode) return <ResumoPage {...props} />;
  return <Navigate to={(ROLE_HOME && ROLE_HOME[papel]) || '/tarefas'} replace />;
}

// ===== Modal Manager =====
function useModalStack() {
  const [stack, setStack] = useStateApp([]);
  const open = (kind, props = {}) => setStack((s) => [...s, { kind, props, id: Math.random().toString(36).slice(2) }]);
  const close = () => setStack((s) => s.slice(0, -1));
  const closeAll = () => setStack([]);
  return { stack, open, close, closeAll };
}

// ===== Pipeline filter bar with funnel switcher =====
const PERIODOS = [
{ id: "all", label: "Todo período", dias: null },
{ id: "mes", label: "Mês", dias: 30 },
{ id: "tri", label: "Trimestre", dias: 90 },
{ id: "sem", label: "Semestre", dias: 180 },
{ id: "ano", label: "Anual", dias: 365 }];

const CALORES = [
{ id: "cold", label: "Frio", color: "var(--color-cool)" },
{ id: "warm", label: "Morno", color: "var(--color-warn)" },
{ id: "hot", label: "Quente", color: "var(--color-corten)" }];


// ---- Filtros do pipeline: toolbar limpa + popover organizado + chips de filtros ativos ----
function FilterPill({ on, color, children, onClick }) {
  return (
    <button type="button" className={`fpill ${on ? "on" : ""}`} onClick={onClick}>
      {color && <span className="dot" style={{ background: color }} />}
      {children}
      {on && <Icon name="check" size={11} style={{ marginLeft: 2 }} />}
    </button>);

}

function FilterPopover({ filters, update, clearAll, activeCount, especificadores, activeFunnel, losts, onClose }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const onDoc = (e) => {if (ref.current && !ref.current.contains(e.target)) onClose();};
    const onKey = (e) => {if (e.key === "Escape") onClose();};
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {document.removeEventListener("mousedown", onDoc);document.removeEventListener("keydown", onKey);};
  }, [onClose]);
  const toggle = (k, v) => update(k, filters[k] === v ? "all" : v);

  return (
    <div className="fpop" ref={ref}>
      <div className="fpop-scroll">
        <div className="fpop-sec">
          <div className="fpop-lb">Calor do negócio</div>
          <div className="fpop-row">
            {CALORES.map((c) =>
            <FilterPill key={c.id} on={filters.calor === c.id} color={c.color} onClick={() => toggle("calor", c.id)}>{c.label}</FilterPill>
            )}
          </div>
        </div>

        <div className="fpop-sec">
          <div className="fpop-lb">Tipo de cliente</div>
          <div className="fpop-row">
            {TIPO_CLIENTE.filter((t) => t.funil === activeFunnel).map((t) =>
            <FilterPill key={t.id} on={filters.tipo === t.id} color={t.color} onClick={() => toggle("tipo", t.id)}>{t.short}</FilterPill>
            )}
          </div>
        </div>

        <div className="fpop-sec">
          <div className="fpop-lb">Origem do lead</div>
          <div className="fpop-row">
            <FilterPill on={filters.origem === "agente"} onClick={() => toggle("origem", "agente")}><Icon name="cpu" size={12} /> Agente IA</FilterPill>
            <FilterPill on={filters.origem === "humano"} onClick={() => toggle("origem", "humano")}><Icon name="user" size={12} /> Humano</FilterPill>
          </div>
        </div>

        <div className="fpop-sec">
          <div className="fpop-lb">Período de criação</div>
          <div className="fpop-row">
            {PERIODOS.filter((p) => p.id !== "all").map((p) =>
            <FilterPill key={p.id} on={filters.periodo === p.id} onClick={() => toggle("periodo", p.id)}>{p.label}</FilterPill>
            )}
          </div>
        </div>

        <div className="fpop-sec">
          <div className="fpop-lb">Canal / Especificador</div>
          <Select value={filters.canal} onChange={(v) => update("canal", v)}
            options={[{ value: "all", label: "Todos os canais" }, ...especificadores.map((e) => ({ value: e.id, label: e.nome }))]} />
        </div>
      </div>

      <div className="fpop-foot">
        <button type="button" className="btn ghost sm" onClick={clearAll} disabled={activeCount === 0} style={{ opacity: activeCount === 0 ? 0.4 : 1 }}>
          Limpar tudo
        </button>
        <button type="button" className="btn primary sm" onClick={onClose}>Pronto</button>
      </div>
    </div>);

}

function PipelineFilters({ filters, setFilters, leads, allLeads, onOpenNewLead, activeFunnel, setActiveFunnel, especificadores }) {
  const update = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  // Mesma permissão que o menu "+" já conferia, e que o banco passou a cobrar na 0088.
  const podeCriarLead = usePermission('pipeline.create_lead');
  const [popOpen, setPopOpen] = useStateApp(false);
  const abertos = leads.filter((l) => l.stage !== "GANHO" && !l.perdido);
  const totalShown = abertos.length;
  const totalValue = abertos.reduce((s, l) => s + (l.valor || 0), 0);
  const losts = allLeads.filter((l) => l.perdido).length;

  const chips = [];
  if (filters.calor !== "all") {const c = CALORES.find((c) => c.id === filters.calor);chips.push({ k: "calor", lb: "Calor", v: c.label, color: c.color });}
  if (filters.tipo !== "all") {const t = TIPO_CLIENTE_BY_ID[filters.tipo];chips.push({ k: "tipo", lb: "Tipo", v: t?.short, color: t?.color });}
  if (filters.origem !== "all") chips.push({ k: "origem", lb: "Origem", v: filters.origem === "agente" ? "Agente IA" : "Humano" });
  if (filters.periodo !== "all") chips.push({ k: "periodo", lb: "Período", v: PERIODOS.find((p) => p.id === filters.periodo)?.label });
  if (filters.canal !== "all") chips.push({ k: "canal", lb: "Canal", v: especificadores.find((e) => e.id === filters.canal)?.nome });
  const activeCount = chips.length;

  const clearAll = () => setFilters((f) => ({ ...f, calor: "all", tipo: "all", canal: "all", periodo: "all", origem: "all", showLost: false }));
  const removeChip = (k) => update(k, k === "showLost" ? false : "all");

  return (
    <>
      <div className="pipeline-funnel-bar">
        <FunnelSwitcher value={activeFunnel} onChange={setActiveFunnel} />
        <div className="pf-direita">
          <div className="pf-resumo" style={{ fontSize: 12, color: "var(--color-ink-soft)" }}>
            <span style={{ fontFamily: "var(--font-mono)", fontWeight: 700, color: "var(--color-ink)" }}>{totalShown}</span> em aberto ·{" "}
            <span style={{ fontFamily: "var(--font-mono)", fontWeight: 700, color: "var(--color-ink)" }}>{brlCompact(totalValue)}</span> em pipeline
          </div>
          {podeCriarLead && <button className="btn primary pf-novo" onClick={onOpenNewLead}><Icon name="plus" size={13} /> Novo negócio</button>}
        </div>
      </div>

      <div className="ptoolbar">
        <div className="seg">
          <button className={filters.view === "kanban" ? "on" : ""} onClick={() => update("view", "kanban")} title="Kanban" aria-label="Kanban">
            <Icon name="layout-kanban" size={13} /> <span className="lbl">Kanban</span>
          </button>
          <button className={filters.view === "table" ? "on" : ""} onClick={() => update("view", "table")} title="Tabela" aria-label="Tabela">
            <Icon name="file-text" size={13} /> <span className="lbl">Tabela</span>
          </button>
        </div>

        <div className="fb-search">
          <Icon name="search" size={13} style={{ color: "var(--color-ink-soft)" }} />
          <input
            placeholder="Buscar negócio, contato ou segmento…"
            value={filters.q}
            onChange={(e) => update("q", e.target.value)} />
          
          {filters.q && <button className="icon-btn" style={{ width: 22, height: 22 }} onClick={() => update("q", "")}><Icon name="x" size={12} /></button>}
        </div>

        <div className="filters-anchor">
          <button className={`btn btn-filters ${activeCount > 0 ? "has-active" : ""} ${popOpen ? "open" : ""}`} onClick={() => setPopOpen((o) => !o)}
            title="Filtros" aria-label={activeCount > 0 ? `Filtros (${activeCount})` : "Filtros"}>
            <Icon name="filter" size={13} />
            <span className="lbl">Filtros</span>
            {activeCount > 0 && <span className="bf-count">{activeCount}</span>}
            <Icon name="chevron-down" size={12} className="lbl" style={{ opacity: 0.6 }} />
          </button>
          {popOpen &&
          <FilterPopover
            filters={filters} update={update} clearAll={clearAll}
            activeCount={activeCount} especificadores={especificadores}
            activeFunnel={activeFunnel} losts={losts}
            onClose={() => setPopOpen(false)} />

          }
        </div>

        <button
          type="button"
          className={`btn btn-lost ${filters.showLost ? "on" : ""}`}
          onClick={() => update("showLost", !filters.showLost)}
          title={filters.showLost ? "Ocultar negócios perdidos" : "Mostrar negócios perdidos nas etapas"}
          aria-label={losts > 0 ? `Perdidos (${losts})` : "Perdidos"}>

          <Icon name={filters.showLost ? "eye" : "eye-off"} size={13} />
          <span className="lbl">Perdidos</span>
          {losts > 0 && <span className="bl-count">{losts}</span>}
        </button>

        {chips.length > 0 &&
        <div className="fchips">
            {chips.map((c) =>
          <span key={c.k} className={`fchip ${c.danger ? "danger" : ""}`}>
                {c.color && <span className="dot" style={{ background: c.color }} />}
                {c.lb && <span className="fc-lb">{c.lb}</span>}
                <span className="fc-v">{c.v}</span>
                <button type="button" className="fc-x" onClick={() => removeChip(c.k)} aria-label={`Remover filtro ${c.lb || c.v}`}><Icon name="x" size={10} /></button>
              </span>
          )}
            <button type="button" className="fb-clear" onClick={clearAll}>Limpar</button>
          </div>
        }
      </div>
    </>);

}

function PipelinePage({ filters, setFilters, onOpenLead, onOpenNewLead, cardMode }) {
  const store = useStore();
  const activeFunnel = store.activeFunnel;
  const periodoCfg = PERIODOS.find((p) => p.id === filters.periodo) || PERIODOS[0];

  const funnelLeads = useMemoApp(() => store.leads.filter((l) => l.funil === activeFunnel), [store.leads, activeFunnel]);

  const filtered = useMemoApp(() => {
    const cutoff = periodoCfg.dias ? new Date(TODAY.getTime() - periodoCfg.dias * 24 * 60 * 60 * 1000) : null;
    return funnelLeads.filter((l) => {
      if (!filters.showLost && l.perdido) return false;
      if (filters.tipo !== "all" && l.tipoCliente !== filters.tipo) return false;
      if (filters.origem !== "all" && l.origem !== filters.origem) return false;
      if (filters.calor !== "all" && l.calor !== filters.calor) return false;
      if (filters.canal !== "all" && l.especificadorId !== filters.canal) return false;
      if (cutoff && l.criadoData && l.criadoData < cutoff) return false;
      if (filters.q) {
        const q = filters.q.toLowerCase();
        if (!l.empresa.toLowerCase().includes(q) && !l.contato.toLowerCase().includes(q) && !(l.segmento || "").toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [funnelLeads, filters, periodoCfg]);

  return (
    <div className="main pipeline-main">
      <PipelineFilters
        filters={filters} setFilters={setFilters}
        leads={filtered}
        allLeads={funnelLeads}
        onOpenNewLead={onOpenNewLead}
        activeFunnel={activeFunnel}
        setActiveFunnel={store.setActiveFunnel}
        especificadores={store.especificadores} />
      
      {filters.view === "kanban" ?
      <KanbanBoard leads={filtered} onCardOpen={onOpenLead} toast={store.toast} cardMode={cardMode} funilId={activeFunnel} /> :

      <LeadTable leads={filtered} onOpen={onOpenLead} />
      }
    </div>);

}

function LeadTable({ leads, onOpen }) {
  const store = useStore();
  const [sort, setSort] = useStateApp({ k: "valor", dir: "desc" });

  const sorted = useMemoApp(() => {
    const arr = [...leads];
    arr.sort((a, b) => {
      let av = a[sort.k],bv = b[sort.k];
      if (typeof av === "string") {av = av.toLowerCase();bv = (bv || "").toLowerCase();}
      if (av < bv) return sort.dir === "asc" ? -1 : 1;
      if (av > bv) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [leads, sort]);

  const setSortKey = (k) => setSort((s) => s.k === k ? { k, dir: s.dir === "asc" ? "desc" : "asc" } : { k, dir: "asc" });

  const Th = ({ k, label, num }) =>
  <th onClick={() => setSortKey(k)} style={{ cursor: "pointer", userSelect: "none", textAlign: num ? "right" : "left" }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        {label}
        {sort.k === k && <Icon name="chevron-down" size={11} style={{ transform: sort.dir === "asc" ? "rotate(180deg)" : "none" }} />}
      </span>
    </th>;


  return (
    <div style={{ padding: 22 }}>
      <div className="card-section">
        <table className="data-table">
          <thead>
            <tr>
              <Th k="empresa" label="Negócio" />
              <Th k="tipoCliente" label="Tipo" />
              <Th k="stage" label="Estágio" />
              <Th k="valor" label="Valor" num />
              <th>Cliente</th>
              <th>Canal</th>
              <th>Próxima Ação</th>
              <th style={{ textAlign: "right" }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((l) => {
              const tipo = TIPO_CLIENTE_BY_ID[l.tipoCliente];
              const cliente = l.clienteId ? store.getClienteById(l.clienteId) : null;
              const esp = l.especificadorId ? store.getEspecificadorById(l.especificadorId) : null;
              return (
                <tr key={l.id} style={{ cursor: "pointer" }} onClick={() => onOpen(l.id)}>
                  <td>
                    <div className="co">
                      <div className="av" style={{ background: tipo?.tone, color: tipo?.color }}>{l.empresa.split(" ").map((w) => w[0]).slice(0, 2).join("")}</div>
                      <div>
                        <div style={{ fontWeight: 700 }}>{l.empresa}</div>
                        <div style={{ fontSize: 11, color: "var(--color-ink-soft)" }}>{l.contato}</div>
                      </div>
                    </div>
                  </td>
                  <td>{tipo && <TipoChip tipo={tipo} size="sm" />}</td>
                  <td>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600 }}>
                      <span className={`stage-dot stage-${l.stage}`} style={{ width: 6, height: 6, borderRadius: "50%" }} />
                      {STAGE_NAMES[l.stage]}
                    </span>
                  </td>
                  <td style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontWeight: 700, color: l.valor > 0 ? "var(--color-ok)" : "var(--color-ink-faint)" }}>{l.valor > 0 ? brl(l.valor) : "—"}</td>
                  <td>{cliente ? <span style={{ fontSize: 12 }}>{cliente.nome}</span> : <span style={{ color: "var(--color-ink-faint)" }}>—</span>}</td>
                  <td>{esp ? <span style={{ fontSize: 12 }}>{esp.nome} <span className="mono" style={{ color: "var(--color-ink-faint)", fontSize: 11 }}>· {esp.pctRepasse}%</span></span> : <span style={{ color: "var(--color-ink-faint)" }}>—</span>}</td>
                  <td>
                    {l.proximaAcao !== "—" ?
                    <div className={`next-action ${l.proximaDue ? "due" : ""}`} style={{ padding: "4px 8px", display: "inline-flex" }}>
                        <Icon name={l.proximaDue ? "alert-triangle" : "clock"} size={11} />
                        {l.proximaAcao}
                      </div> :
                    <span style={{ color: "var(--color-ink-faint)" }}>—</span>}
                  </td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <button className="icon-btn" onClick={(e) => {e.stopPropagation();onOpen(l.id);}}><Icon name="external-link" size={14} /></button>
                  </td>
                </tr>);

            })}
          </tbody>
        </table>
      </div>
    </div>);

}

// Route map — legacy page IDs → hash routes
const NAV_MAP = {
  resumo: '/', pipeline: '/pipeline', clientes: '/clientes',
  alerts: '/tarefas', produtos: '/produtos', posvenda: '/pos-venda',
  sac: '/sac', chamadoPage: '/sac', reports: '/relatorios',
  gestao: '/gestao', admin: '/admin', ajustes: '/ajustes', webhooks: '/webhooks', logs: '/logs',
  financeiro: '/financeiro',
  perfil: '/perfil',
};

// ===== Inner App (within Store) =====
function AppInner() {
  const store = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [openLeadId, setOpenLeadId] = useStateApp(null);
  const [openClienteId, setOpenClienteId] = useStateApp(null);
  const [openProdutoId, setOpenProdutoId] = useStateApp(null);
  const [clientePageId, setClientePageId] = useStateApp(null);
  const [negocioPageId, setNegocioPageId] = useStateApp(null);
  const [cmdOpen, setCmdOpen] = useStateApp(false);
  const [menuOpen, setMenuOpen] = useStateApp(false);
  const [sidebarPinned, setSidebarPinned] = useStateApp(() => localStorage.getItem('sb_pin') === '1');
  const toggleSidebarPin = () => setSidebarPinned(p => {
    const next = !p;
    localStorage.setItem('sb_pin', next ? '1' : '0');
    return next;
  });
  const [navFilters, setNavFilters] = useStateApp(null);
  const modals = useModalStack();
  const [filters, setFilters] = useStateApp({
    view: "kanban", q: "", tipo: "all", origem: "all", showLost: false,
    calor: "all", canal: "all", periodo: "all"
  });

  const alertCount = store.alerts.filter((a) => !a.feito).length;
  const openLead = store.leads.find((l) => l.id === openLeadId);

  useEffectApp(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdOpen(true);
      }
      if (e.key === "Escape") {
        if (modals.stack.length) return;
        if (cmdOpen) setCmdOpen(false);else
        if (openLeadId) setOpenLeadId(null);else
        if (openClienteId) setOpenClienteId(null);else
        if (openProdutoId) setOpenProdutoId(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cmdOpen, openLeadId, openClienteId, openProdutoId, modals.stack.length]);
  const pageTitleMap = {
    resumo: { t: "Resumo", s: TODAY.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" }) },
    pipeline: { t: "Pipeline", s: store.funis.find((f) => f.id === store.activeFunnel)?.name },
    clientes: { t: "Clientes", s: store.clientes.length + " na carteira" },
    alerts: { t: "Alertas", s: alertCount + " pendentes" },
    produtos: { t: "Produtos", s: (store.produtos || []).length.toLocaleString("pt-BR") + " SKUs no catálogo" },
    clientePage: { t: "Perfil do cliente", s: "Visão completa" },
    negocioPage: { t: "Negócio", s: "Área completa" },
    reports: { t: "Relatórios", s: MONTHS[0].label },
    posvenda: { t: "Pós-venda", s: "Acompanhamento pós-fechamento" },
    chamadoPage: { t: "Chamado SAC", s: "Atendimento ao cliente" },
    sac: { t: "SAC", s: "Atendimento e ocorrências pós-venda" },
    gestao: { t: "Gestão", s: "Metas, repasses e comissões" },
    financeiro: { t: "Financeiro", s: "Comissionamento, repasses e descontos" },
    admin: { t: "Admin", s: "Usuários e permissões" },
    ajustes: { t: "Ajustes", s: "Preços, transportadoras e regras comerciais" },
    perfil: { t: "Meu perfil", s: "Seus dados, preferências e acesso" },
    webhooks: { t: "Webhooks", s: store.webhooks.filter((w) => w.ativo).length + " ativos · " + store.webhooks.length + " no total" },
    logs: { t: "API Logs", s: "Últimas 24h · " + store.apiLogs.length + " requisições" }
  };
  const routePageKey = (() => {
    const p = location.pathname;
    if (p === '/') return 'resumo';
    if (p.startsWith('/sac/')) return 'chamadoPage';
    const m = {'/pipeline':'pipeline','/clientes':'clientes','/tarefas':'alerts','/produtos':'produtos','/pos-venda':'posvenda','/sac':'sac','/relatorios':'reports','/gestao':'gestao','/admin':'admin','/ajustes':'ajustes','/perfil':'perfil','/webhooks':'webhooks','/logs':'logs','/financeiro':'financeiro'};
    return m[p] || 'resumo';
  })();
  const effectivePageKey = clientePageId ? 'clientePage' : negocioPageId ? 'negocioPage' : routePageKey;
  const pt = pageTitleMap[effectivePageKey] || pageTitleMap.resumo;

  // Qualquer navega\u00e7\u00e3o de rota fecha as p\u00e1ginas expandidas de neg\u00f3cio/cliente \u2014 elas
  // ficam por cima das rotas e antes prendiam o usu\u00e1rio at\u00e9 clicar em \"Voltar\".
  useEffectApp(() => { setClientePageId(null); setNegocioPageId(null); }, [location.pathname]);

  const handleNav = (id, opts = null) => {
    setOpenLeadId(null); setOpenClienteId(null); setOpenProdutoId(null);
    setClientePageId(null); setNegocioPageId(null); setNavFilters(opts); setMenuOpen(false);
    const route = NAV_MAP[id]; if (route) navigate(route);
  };
  const handleOpenLead = (id) => {setOpenLeadId(id);setOpenClienteId(null);};
  const handleOpenCliente = (id) => {setOpenClienteId(id);setOpenLeadId(null);};
  const handleFabAction = (id) => {
    if (id === "lead") modals.open("newLead", { defaultFunil: store.activeFunnel });else
    if (id === "cliente") modals.open("newCliente");else
    if (id === "alert") modals.open("newAlert", { leadId: openLeadId, clienteId: openClienteId, lockClient: !!openClienteId });
  };
  const handleMarkLost = (leadId) => {
    modals.open("markLost", { leadId });
  };

  const drawerOpen = !!openLead || !!openClienteId || !!openProdutoId;

  return (
    <div className={`app ${drawerOpen ? "drawer-open" : ""} ${menuOpen ? "menu-open" : ""} sidebar-rail ${sidebarPinned ? "sidebar-pinned" : ""}`} data-screen-label={`BeamCRM / ${pt.t}`}>
      <Sidebar alertCount={alertCount} pinned={sidebarPinned} onPin={toggleSidebarPin} onNavigate={() => setMenuOpen(false)} />
      {menuOpen && <div className="sidebar-backdrop" onClick={() => setMenuOpen(false)} />}
      <Header
        pageTitle={pt.t}
        pageSub={pt.s}
        alertCount={alertCount}
        onOpenCmd={() => setCmdOpen(true)}
        onOpenAlerts={() => handleNav("alerts")}
        onOpenLead={handleOpenLead}
        onToggleMenu={() => setMenuOpen((o) => !o)} />
      

      {/* Se uma tela quebrar, o menu e o cabecalho seguem de pe (src/erro-na-tela.jsx). A chave e "onde a pessoa esta":
          trocar de rota limpa o erro. */}
      <ErroNaTela nivel="tela" chave={`${location.pathname}|${clientePageId || ''}|${negocioPageId || ''}`}
        aoVoltar={() => { setClientePageId(null); setNegocioPageId(null); navigate('/'); }}>
      {clientePageId ? (
        <ClientePage
          clienteId={clientePageId}
          onBack={() => { navigate('/clientes'); setClientePageId(null); }}
          onOpenLead={handleOpenLead}
          onCreateAlert={() => modals.open("newAlert", { clienteId: clientePageId, lockClient: true })}
          onEdit={() => modals.open("editCliente", { clienteId: clientePageId })}
          onPreviewPdf={(anexo) => modals.open("pdfPreview", { anexo })}
          onOpenChamado={(id) => { navigate(`/sac/${id}`); setClientePageId(null); }}
          onNewChamado={(opts = {}) => modals.open("newChamado", opts)} />
      ) : negocioPageId ? (
        <NegocioPage
          leadId={negocioPageId}
          onBack={() => { navigate('/pipeline'); setNegocioPageId(null); }}
          onEdit={() => modals.open("editLead", { lead: store.leads.find(l => l.id === negocioPageId) })}
          onAddNote={() => modals.open("addNote", { leadId: negocioPageId })}
          onAddAlert={() => modals.open("newAlert", { leadId: negocioPageId, clienteId: store.leads.find(l => l.id === negocioPageId)?.clienteId, lockClient: !!store.leads.find(l => l.id === negocioPageId)?.clienteId })}
          onNewProposta={() => modals.open("proposta", { leadId: negocioPageId })}
          onEditProposta={(p) => modals.open("proposta", { leadId: negocioPageId, propostaBase: p })}
          onOpenCliente={handleOpenCliente}
          onNewCliente={(opts) => modals.open("newCliente", opts)}
          onPreviewPdf={(anexo) => modals.open("pdfPreview", { anexo })}
          onMarkLost={() => handleMarkLost(negocioPageId)} />
      ) : (
        <React.Suspense fallback={<TelaCarregando />}>
        <Routes>
          <Route path="/" element={<HomeRoute onOpenLead={handleOpenLead} onNav={handleNav} onCreateAlert={() => modals.open("newAlert", {})} onOpenTask={(a) => modals.open("editTask", { alertId: a.id })} onOpenCliente={handleOpenCliente} onOpenMeta={() => modals.open("meta", {})} />} />
          <Route path="/pipeline" element={<Guard perm="pipeline.view"><PipelinePage filters={filters} setFilters={setFilters} onOpenLead={handleOpenLead} onOpenNewLead={() => modals.open("newLead", { defaultFunil: store.activeFunnel })} /></Guard>} />
          <Route path="/clientes" element={<Guard perm="clientes.view"><ClientesPage onOpenCliente={handleOpenCliente} onOpenLead={handleOpenLead} onNewCliente={() => modals.open("newCliente")} initialFilters={navFilters} /></Guard>} />
          <Route path="/tarefas" element={<Guard perm="tarefas.view"><AlertasCalendarioPage onOpenLead={handleOpenLead} onOpenCliente={handleOpenCliente} onCreateAlert={() => modals.open("newAlert", {})} onEditTask={(a) => modals.open("editTask", { alertId: a.id })} /></Guard>} />
          <Route path="/produtos" element={<Guard perm="produtos.view"><ProdutosPage onOpenProduto={setOpenProdutoId} /></Guard>} />
          <Route path="/pos-venda" element={<Guard perm="posvenda.view">{telaOuEmDesenvolvimento('/pos-venda', <PosVendaPage onOpenLead={handleOpenLead} />)}</Guard>} />
          <Route path="/sac" element={<Guard perm="sac.view">{telaOuEmDesenvolvimento('/sac', <SacPage onOpenChamado={(id) => navigate(`/sac/${id}`)} onNewChamado={(opts = {}) => modals.open("newChamado", opts)} />)}</Guard>} />
          <Route path="/sac/:id" element={<Guard perm="sac.view">{telaOuEmDesenvolvimento('/sac', <ChamadoPage onBack={() => navigate('/sac')} onOpenCliente={handleOpenCliente} onOpenLead={handleOpenLead} />)}</Guard>} />
          <Route path="/relatorios" element={<Guard perm="relatorios.view_comissoes"><ReportsPage onOpenLead={handleOpenLead} onNav={handleNav} onOpenCliente={handleOpenCliente} onOpenCohortCell={(row, col, value, detalhe) => modals.open("cohortCell", { row, col, value, detalhe })} /></Guard>} />
          <Route path="/financeiro" element={<Guard perm="financeiro.view">{telaOuEmDesenvolvimento('/financeiro', <FinanceiroPage />)}</Guard>} />
          <Route path="/gestao" element={<Guard perm="gestao.view"><GestaoPage onOpenLead={handleOpenLead} /></Guard>} />
          <Route path="/admin" element={<Guard perm="admin.view"><AdminPage /></Guard>} />
          <Route path="/ajustes" element={<Guard perm="ajustes.view">{telaOuEmDesenvolvimento('/ajustes', <AjustesPage />)}</Guard>} />
          <Route path="/perfil" element={<PerfilPage />} />
          <Route path="/webhooks" element={<Guard perm="admin.view">{telaOuEmDesenvolvimento('/webhooks', <WebhooksPage onCreate={() => modals.open("newWebhook")} />)}</Guard>} />
          <Route path="/logs" element={<Guard perm="admin.view"><ApiLogsPage onRowClick={(log) => modals.open("apiLogDetail", { log })} /></Guard>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </React.Suspense>
      )}
      </ErroNaTela>

      {/* Gavetas e modais: se uma quebrar, aparece um aviso pequeno e o resto do CRM continua usavel. */}
      <ErroNaTela nivel="painel" chave={`${openLeadId}|${openClienteId}|${openProdutoId}|${modals.stack.map((m) => m.id).join(',')}`}
        onFechar={() => { setOpenLeadId(null); setOpenClienteId(null); setOpenProdutoId(null); modals.closeAll(); }}>
      {openLead &&
      <LeadDrawer
        lead={openLead}
        onClose={() => setOpenLeadId(null)}
        onEdit={() => modals.open("editLead", { lead: openLead })}
        onAddNote={() => modals.open("addNote", { leadId: openLead.id })}
        onAddAlert={() => modals.open("newAlert", { leadId: openLead.id, clienteId: openLead.clienteId, lockClient: !!openLead.clienteId })}
        onOpenCliente={handleOpenCliente}
        onNewCliente={(opts) => modals.open("newCliente", opts)}
        onMarkLost={() => handleMarkLost(openLead.id)}
        onPreviewPdf={(anexo) => modals.open("pdfPreview", { anexo })}
        onNewProposta={() => modals.open("proposta", { leadId: openLead.id })}
        onEditProposta={(p) => modals.open("proposta", { leadId: openLead.id, propostaBase: p })}
        onExpandToPage={() => {setNegocioPageId(openLead.id);setOpenLeadId(null);}} />

      }
      {openClienteId && !openLead &&
      <ClienteDrawer
        clienteId={openClienteId}
        onClose={() => setOpenClienteId(null)}
        onOpenLead={handleOpenLead}
        onCreateAlert={() => modals.open("newAlert", { clienteId: openClienteId, lockClient: true })}
        onEdit={() => modals.open("editCliente", { clienteId: openClienteId })}
        onExpandToPage={() => {setClientePageId(openClienteId);setOpenClienteId(null);}} />

      }
      {openProdutoId && !openLead && !openClienteId &&
      <ProdutoDrawer
        produtoId={openProdutoId}
        onClose={() => setOpenProdutoId(null)} />

      }

      <FAB onAction={handleFabAction} />
      <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} onNav={handleNav} onOpenLead={handleOpenLead} leads={store.leads} buscarPedido={api ? api.buscarPedidoPorReferencia : null} />

      {/* Modal stack */}
      {modals.stack.map((m) => {
        const props = { ...m.props, onClose: modals.close };
        if (m.kind === "newLead") return <NewLeadModal key={m.id} {...props} />;
        if (m.kind === "newAlert") return <NewAlertModal key={m.id} {...props} />;
        if (m.kind === "addNote") return <AddNoteModal key={m.id} {...props} />;
        if (m.kind === "editLead") return <EditLeadModal key={m.id} {...props} />;
        if (m.kind === "newWebhook") return <NewWebhookModal key={m.id} {...props} />;
        if (m.kind === "apiLogDetail") return <ApiLogDetailModal key={m.id} {...props} />;
        if (m.kind === "cohortCell") return <CohortCellModal key={m.id} {...props} />;
        if (m.kind === "newCliente") return <NewClienteModal key={m.id} {...props} />;
        if (m.kind === "markLost") return <MarkLostInline key={m.id} {...props} />;
        if (m.kind === "editTask") return <EditTaskModal key={m.id} {...props} />;
        if (m.kind === "meta") return <MetaModal key={m.id} {...props} />;
        if (m.kind === "pdfPreview") return <PdfPreviewModal key={m.id} {...props} />;
        if (m.kind === "proposta") return <PropostaBuilderModal key={m.id} {...props} onEditCliente={(cid) => modals.open("editCliente", { clienteId: cid })} onNewCliente={(opts) => modals.open("newCliente", opts)} />;
        if (m.kind === "editCliente") return <EditClienteModal key={m.id} {...props} />;
        if (m.kind === "newChamado") return <NewChamadoModal key={m.id} {...props} />;
        return null;
      })}
      </ErroNaTela>

      <ToastLayer toasts={store.toasts} />
    </div>);

}

// Wrapper that uses kanban's LostReasonModal for ad-hoc lost-marking
function MarkLostInline({ leadId, onClose }) {
  const store = useStore();
  const lead = store.leads.find((l) => l.id === leadId);
  if (!lead) return null;
  return (
    <LostReasonModal
      lead={lead}
      onCancel={onClose}
      onConfirm={(motivo, obs) => {
        store.markLeadLost(leadId, motivo, obs);
        onClose();
      }} />);


}

function App() {
  const [sessao, setSessao] = useStateApp(null);
  const [carregando, setCarregando] = useStateApp(true);
  // Link de acesso que veio pelo nosso dominio (?token_hash=...): a tela de confirmacao so gasta o token no clique. `avisoDoLink` leva a
  // mensagem para a tela de login quando a confirmacao falha (expirado, ja usado).
  const [link, setLink] = useStateApp(linkDeAcesso);
  const [avisoDoLink, setAvisoDoLink] = useStateApp(null);
  // Quem ja esta logado e clica num link de acesso: o token nao e mais necessario; so sai do endereco.
  useEffectApp(() => {
    if (sessao && link) {
      window.history.replaceState(null, '', enderecoSemOLink(window.location.href));
      setLink(null);
    }
  }, [sessao, link]);

  // A sessão vem do Supabase, não do front: lerSessao() confere o que já
  // está guardado (reload) e onAuthStateChange cobre tanto o retorno do
  // magic link (SIGNED_IN) quanto o logout (SIGNED_OUT), em qualquer aba.
  useEffectApp(() => {
    let ativo = true;
    lerSessao()
      .then((s) => { if (ativo) setSessao(s); })
      .finally(() => { if (ativo) setCarregando(false); });
    const { data: assinatura } = supabase.auth.onAuthStateChange((_evento, session) => {
      setSessao(session);
      setCarregando(false);
    });
    return () => { ativo = false; assinatura.subscription.unsubscribe(); };
  }, []);

  // O perfil é buscado depois da sessão porque depende do usuário logado.
  // `undefined` = ainda buscando; `null` = não existe cadastro.
  const [perfil, setPerfil] = useStateApp(undefined);
  const [erroPerfil, setErroPerfil] = useStateApp(null);
  const [tentativa, setTentativa] = useStateApp(0);
  const idUsuario = sessao?.user?.id || null;

  useEffectApp(() => {
    if (!idUsuario) { setPerfil(undefined); setErroPerfil(null); return; }
    let vivo = true;
    setPerfil(undefined);
    setErroPerfil(null);
    perfilAtual(idUsuario)
      .then((p) => { if (vivo) setPerfil(p); })
      .catch((e) => { if (vivo) setErroPerfil(e?.message || "Erro desconhecido."); });
    return () => { vivo = false; };
  }, [idUsuario, tentativa]);

  // Logout automatico por inatividade (2h). O hook so age quando ha sessao;
  // ao expirar, marca o motivo (para a tela de login explicar) e encerra a
  // sessao SO deste navegador (escopo local). Ver src/inatividade.js.
  const aoExpirarPorInatividade = React.useCallback(() => {
    marcarLogoutPorInatividade();
    limparAtividade();             // a marca morre com a sessao que a criou
    sairLocal().catch(() => {});   // onAuthStateChange derruba a sessao -> LoginScreen
  }, []);

  // O relogio de inatividade pertence a ESTA sessao, e o identificador vem da
  // claim `session_id` do token (conferido: o JWT do projeto a traz).
  //
  // POR QUE NAO O id DO USUARIO: ele e o mesmo amanha. A marca de hoje
  // continuaria valendo para a sessao de amanha, e a pessoa entraria pelo magic
  // link so para ser expulsa na hora -- que e exatamente o defeito que isto
  // conserta. `access_token` tambem nao serve: muda a cada renovacao, e ai o
  // relogio reiniciaria sozinho de hora em hora.
  const sessaoId = React.useMemo(() => {
    const t = sessao?.access_token;
    if (!t) return null;
    try {
      return JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).session_id ?? null;
    } catch { return null; }
  }, [sessao?.access_token]);

  const inatividade = useMonitorInatividade({
    ativo: !!sessao,
    onExpira: aoExpirarPorInatividade,
    opcoes: { sessaoId },
  });
  const avisoInatividade = (sessao && inatividade.avisando)
    ? <AvisoInatividade
        restanteMs={inatividade.restanteMs}
        onContinuar={inatividade.continuar}
        onSair={aoExpirarPorInatividade}
      />
    : null;

  if (carregando) return null;
  if (!sessao) {
    return link
      ? <ConfirmarAcesso link={link} onFalha={(aviso) => { setAvisoDoLink(aviso); setLink(null); }} />
      : <LoginScreen avisoInicial={avisoDoLink} />;
  }

  if (erroPerfil) {
    return (
      <React.Fragment>
        {avisoInatividade}
        <FalhaAoCarregarPerfil
          erro={erroPerfil}
          onTentarDeNovo={() => setTentativa((n) => n + 1)}
          onSair={encerrarSessao}
        />
      </React.Fragment>);
  }

  if (perfil === undefined) return null;              // buscando o perfil

  // Nenhum perfil, ou perfil desativado pelo administrador: não entra.
  if (perfil === null || perfil.ativo === false) {
    return (
      <React.Fragment>
        {avisoInatividade}
        <AguardandoLiberacao
          usuario={usuarioDaSessao(sessao)}
          semPerfil={perfil === null}
          onSair={encerrarSessao}
        />
      </React.Fragment>);
  }

  return (
    <React.Fragment>
      {avisoInatividade}
      <StoreProvider usuarioAutenticado={usuarioDoPerfil(perfil, sessao)} api={api}>
        <HashRouter>
          <AppInner />
        </HashRouter>
      </StoreProvider>
    </React.Fragment>);

}

// PipelineFilters: exportado para dois testes: o do botao "Novo negocio" (tests/unit/funil-tela-respeita-a-matriz.test.js)
// e o do layout no celular (tests/unit/pipeline-celular.test.js).
export { App, PipelineFilters };